import { useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Download,
  Minus,
  RefreshCw,
  Save,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FeedbackMessage } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import {
  AUTOMATION_LABEL,
  LOG_KPI_IDS,
  SOURCE_LABEL,
  formatKpiValue,
  kpiImproved,
  kpiOnTarget,
  kpiWaitHint,
  qmWaitingFor,
  type KpiDef,
} from "@/features/kpi/definitions";
import { periodLabel, shiftPeriod, type KpiEntry, type KpiPeriodType } from "@/features/kpi/repo";
import { KpiLogPanel } from "@/features/kpi/KpiLogPanel";

export function KpiAutomationSummary() {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <span className="rounded-full bg-green-100 px-2.5 py-1 font-medium text-green-800">
        18 automatic
      </span>
      <span className="rounded-full bg-accent-400/20 px-2.5 py-1 font-medium text-accent-600">
        13 semi-automatic
      </span>
      <span className="rounded-full bg-brand-100 px-2.5 py-1 font-medium text-brand-800">
        16 missing a source
      </span>
    </div>
  );
}

export function KpiTestGuide() {
  return (
    <div className="mt-6 rounded-xl border border-brand-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-brand-900">Ready to test, department by department</h2>
      <p className="mt-1 text-sm text-brand-500">
        Click <strong>Load automatic values</strong> to show the last shared pull
        (every admin sees the same numbers). Use <strong>Refresh from systems</strong>
        only when you need a new live ask (about a minute).
      </p>
      <ul className="mt-3 grid gap-2 text-sm text-brand-700 md:grid-cols-2">
        <li><strong>Fleet</strong> — should fill from Fleetio (PM, breakdowns, availability, downtime, cost/mile).</li>
        <li><strong>Company / Tank / Van dispatch / Sales rate rows</strong> — revenue, miles, and rate from QuickManage.</li>
        <li><strong>Driver utilization</strong> — Samsara HOS on Dry Van Dispatch.</li>
        <li><strong>On-time + billing cycle</strong> — fill after the status recorder has a few days of history.</li>
        <li><strong>Safety CSA / HazMat</strong> — stay empty until the free FMCSA webkey.</li>
        <li><strong>Accounting</strong> — type in until they name the system (Q6). Settlement Accuracy / Timeliness wait on QuickManage payroll (note on the row). OR / cash / AR are not payroll.</li>
        <li><strong>Tank / Van classified rows</strong> — already fill from trip dollars/miles + trailer make. Get Trailer by id is not required.</li>
      </ul>
    </div>
  );
}

export function KpiBackendNotice({ backend }: { backend: "supabase" | "local" }) {
  if (backend !== "local") return null;
  return (
    <FeedbackMessage tone="info" className="mt-4">
      Preview mode: values, action items, and event logs are saved in this browser
      only. On the live site, whoever loaded last writes the shared numbers — other
      admins see the same pull and do not ask Fleetio again. Use
      <strong> Refresh from systems</strong> only when you need a new pull.
    </FeedbackMessage>
  );
}

export function KpiPeriodToolbar({
  periodType,
  periodStart,
  onPeriodType,
  onPeriodStart,
  entered,
  total,
  dirtyCount,
  saving,
  saved,
  onSave,
  showSave = true,
  collecting = false,
  onCollect,
  lastCollectAt,
  collectFresh = false,
  onExport,
}: {
  periodType: KpiPeriodType;
  periodStart: string;
  onPeriodType: (type: KpiPeriodType) => void;
  onPeriodStart: (start: string) => void;
  entered: number;
  total: number;
  dirtyCount: number;
  saving: boolean;
  saved: boolean;
  onSave: () => void;
  showSave?: boolean;
  collecting?: boolean;
  onCollect?: (force?: boolean) => void;
  lastCollectAt?: string | null;
  collectFresh?: boolean;
  onExport?: () => void;
}) {
  // Monthly-only for now (owner, 2026-08-17). Add "week" back here to restore
  // the weekly view; the rest of the period plumbing still supports it.
  const periodTypes: readonly KpiPeriodType[] = ["month"];
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {periodTypes.length > 1 && (
        <div className="inline-flex overflow-hidden rounded-lg border border-brand-200">
          {periodTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => onPeriodType(type)}
              className={
                "px-3 py-1.5 text-sm font-medium transition-colors " +
                (periodType === type
                  ? "bg-brand-900 text-white"
                  : "bg-white text-brand-600 hover:bg-brand-50")
              }
            >
              {type === "month" ? "Monthly" : "Weekly"}
            </button>
          ))}
        </div>
      )}
      <div className="inline-flex items-center gap-1 rounded-lg border border-brand-200 bg-white px-1 py-1">
        <button
          type="button"
          aria-label="Previous period"
          onClick={() => onPeriodStart(shiftPeriod(periodStart, periodType, -1))}
          className="rounded-md p-1 text-brand-600 hover:bg-brand-50"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="min-w-[10rem] text-center text-sm font-medium">
          {periodLabel(periodStart, periodType)}
        </span>
        <button
          type="button"
          aria-label="Next period"
          onClick={() => onPeriodStart(shiftPeriod(periodStart, periodType, 1))}
          className="rounded-md p-1 text-brand-600 hover:bg-brand-50"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <span className="text-sm text-brand-500">{entered}/{total} entered</span>
      {onExport && (
        <Button variant="outline" onClick={onExport}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      )}
      {onCollect && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => onCollect(false)} disabled={collecting || saving}>
            <RefreshCw className={"mr-2 h-4 w-4" + (collecting ? " animate-spin" : "")} />
            {collecting ? "Loading values…" : "Load automatic values"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onCollect(true)}
            disabled={collecting || saving}
            title="Ask Fleetio, Samsara, and QuickManage again. Wait about a minute."
          >
            Refresh from systems
          </Button>
          {lastCollectAt && (
            <span className="text-xs text-brand-500">
              Last pull {new Date(lastCollectAt).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
              {collectFresh ? " · shared last pull — Refresh to ask systems again" : ""}
            </span>
          )}
        </div>
      )}
      {showSave && (
        <div className="ml-auto flex items-center gap-3">
          {saved && dirtyCount === 0 && <span className="text-sm text-green-700">Saved.</span>}
          <Button onClick={onSave} disabled={saving || dirtyCount === 0}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Saving…" : `Save changes${dirtyCount ? ` (${dirtyCount})` : ""}`}
          </Button>
        </div>
      )}
    </div>
  );
}

export function KpiTable({
  definitions,
  current,
  previous,
  allEntries,
  draft,
  onDraftChange,
  dirtyIds,
  readOnly = false,
  periodType,
  periodStart,
  userId,
  onLogSynced,
}: {
  definitions: KpiDef[];
  current: Map<string, KpiEntry>;
  previous: Map<string, KpiEntry>;
  allEntries: KpiEntry[];
  draft: Record<string, string>;
  onDraftChange: (id: string, value: string) => void;
  dirtyIds: string[];
  readOnly?: boolean;
  periodType: KpiPeriodType;
  periodStart: string;
  userId?: string;
  onLogSynced?: () => void;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] text-sm">
        <thead>
          <tr className="border-b border-brand-100 text-left text-xs uppercase tracking-wide text-brand-400">
            <th className="w-8 py-2 pr-2 font-medium" />
            <th className="py-2 pr-4 font-medium">KPI</th>
            <th className="py-2 pr-4 font-medium">Target</th>
            <th className="py-2 pr-4 font-medium">Actual</th>
            <th className="py-2 pr-4 font-medium">Previous</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 pr-4 font-medium">Data source</th>
            <th className="py-2 font-medium">Owner</th>
          </tr>
        </thead>
        <tbody>
          {definitions.map((def) => {
            const isOpen = expanded.has(def.id);
            const raw = (draft[def.id] ?? "").trim();
            const value = raw === "" ? null : Number(raw);
            const valid = value !== null && Number.isFinite(value);
            const previousEntry = previous.get(def.id);
            const onTarget = valid ? kpiOnTarget(def, value) : null;
            const improved =
              valid && previousEntry ? kpiImproved(def, value, previousEntry.value) : null;
            const history = allEntries
              .filter((entry) => entry.kpiId === def.id)
              .sort((a, b) => b.periodStart.localeCompare(a.periodStart))
              .slice(0, 12);

            return (
              <KpiTableRow
                key={def.id}
                def={def}
                isOpen={isOpen}
                onToggle={() => toggle(def.id)}
                raw={raw}
                value={value}
                valid={valid}
                previousEntry={previousEntry}
                onTarget={onTarget}
                improved={improved}
                history={history}
                dirty={dirtyIds.includes(def.id)}
                readOnly={readOnly}
                onDraftChange={(next) => onDraftChange(def.id, next)}
                currentEntry={current.get(def.id)}
                periodType={periodType}
                periodStart={periodStart}
                userId={userId}
                onLogSynced={onLogSynced}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function KpiTableRow({
  def,
  isOpen,
  onToggle,
  raw,
  value: rawValue,
  valid: rawValid,
  previousEntry,
  onTarget: rawOnTarget,
  improved: rawImproved,
  history,
  dirty,
  readOnly,
  onDraftChange,
  currentEntry,
  periodType,
  periodStart,
  userId,
  onLogSynced,
}: {
  def: KpiDef;
  isOpen: boolean;
  onToggle: () => void;
  raw: string;
  value: number | null;
  valid: boolean;
  previousEntry?: KpiEntry;
  onTarget: boolean | null;
  improved: boolean | null;
  history: KpiEntry[];
  dirty: boolean;
  readOnly: boolean;
  onDraftChange: (value: string) => void;
  currentEntry?: KpiEntry;
  periodType: KpiPeriodType;
  periodStart: string;
  userId?: string;
  onLogSynced?: () => void;
}) {
  const [override, setOverride] = useState(false);
  // A not-measurable KPI must never render a number, even when a stale auto row
  // is still sitting in kpi_entries from before the collector stopped writing
  // it (INC-002: on-time rows read 8.3% and were mistaken for performance).
  const suppressed = Boolean(def.notMeasurable);
  const value = suppressed ? null : rawValue;
  const valid = suppressed ? false : rawValid;
  const onTarget = suppressed ? null : rawOnTarget;
  const improved = suppressed ? null : rawImproved;
  const autoLocked = !readOnly && !override && (currentEntry?.source === "auto" || (def.automation === "auto" && !currentEntry));
  return (
    <>
      <tr className="border-b border-brand-50">
        <td className="py-2.5 pr-2 align-top">
          <button
            type="button"
            onClick={onToggle}
            aria-label={`${isOpen ? "Hide" : "Show"} ${def.name} details`}
            className="rounded p-1 text-brand-400 hover:bg-brand-50 hover:text-brand-700"
          >
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </td>
        <td className="max-w-sm py-2.5 pr-4">
          <div className="font-medium text-brand-900">{def.name}</div>
          <div className="text-xs text-brand-400">{def.formula}</div>
          {def.staffKeepAlive ? (
            <div className="mt-1 text-xs font-medium text-red-700">{def.staffKeepAlive}</div>
          ) : null}
          {def.notMeasurable ? (
            <div className="mt-1 text-xs font-medium text-slate-600">{def.notMeasurable}</div>
          ) : null}
          {qmWaitingFor(def).map((item) =>
            item.rowNote ? (
              <div key={item.id} className="mt-1 text-xs font-medium text-amber-800">{item.rowNote}</div>
            ) : null,
          )}
        </td>
        <td className="whitespace-nowrap py-2.5 pr-4 text-brand-600">{def.targetLabel}</td>
        <td className="py-2.5 pr-4">
          {readOnly ? (
            valid && value !== null ? formatKpiValue(value, def.unit) : <span className="text-brand-300">—</span>
          ) : autoLocked ? (
            <div>
              {valid && value !== null ? (
                <div className="font-medium text-brand-900">{formatKpiValue(value, def.unit)}</div>
              ) : (
                <div>
                  <div className="text-sm text-brand-400">
                    {suppressed ? "Not measurable" : "Not pulled yet"}
                  </div>
                  <div className="mt-0.5 max-w-[14rem] text-xs text-brand-400">{kpiWaitHint(def)}</div>
                </div>
              )}
              <button
                type="button"
                className="mt-1 text-xs text-brand-500 underline-offset-2 hover:underline"
                onClick={() => setOverride(true)}
              >
                {currentEntry ? "Override" : "Enter manually"}
              </button>
            </div>
          ) : (
            <>
              <Input
                type="number"
                step="any"
                inputMode="decimal"
                value={raw}
                onChange={(event) => onDraftChange(event.target.value)}
                placeholder="—"
                aria-label={`${def.name} actual value`}
                className={"h-9 w-28" + (dirty ? " border-accent-500" : "")}
              />
              {raw !== "" && !valid && <div className="mt-1 text-xs text-red-600">Enter a number</div>}
              {raw === "" && <div className="mt-1 max-w-[14rem] text-xs text-brand-400">{kpiWaitHint(def)}</div>}
            </>
          )}
        </td>
        <td className="whitespace-nowrap py-2.5 pr-4 text-brand-600">
          {previousEntry ? (
            <span className="inline-flex items-center gap-1.5">
              {formatKpiValue(previousEntry.value, def.unit)}
              {improved === true && <TrendingUp className="h-3.5 w-3.5 text-green-600" />}
              {improved === false && <TrendingDown className="h-3.5 w-3.5 text-red-600" />}
              {improved === null && valid && <Minus className="h-3.5 w-3.5 text-brand-300" />}
            </span>
          ) : <span className="text-brand-300">—</span>}
        </td>
        <td className="py-2.5 pr-4">
          {!valid ? <span className="text-brand-300">—</span>
            : Boolean((currentEntry?.detail as { q5?: unknown } | undefined)?.q5)
              ? <Badge>Provisional</Badge>
            : onTarget === true ? <Badge variant="success">On target</Badge>
            : onTarget === false ? <Badge variant="danger">Off target</Badge>
            : improved === true ? <Badge variant="success">Improving</Badge>
            : improved === false ? <Badge variant="danger">Worsening</Badge>
            : <Badge>Tracked</Badge>}
        </td>
        <td className="max-w-[11rem] py-2.5 pr-4">
          {def.automation === "auto" ? <Badge variant="success">Auto</Badge>
            : def.automation === "semi" ? <Badge variant="accent">Semi-auto</Badge>
            : <Badge>No source</Badge>}
          <div className="mt-1 text-xs text-brand-400">
            {def.sources.filter((source) => source !== "manual").map((source) => SOURCE_LABEL[source]).join(" + ")
              || "Nothing records this today"}
          </div>
        </td>
        <td className="whitespace-nowrap py-2.5 text-brand-600">{def.owner}</td>
      </tr>
      {isOpen && (
        <tr className="border-b border-brand-100 bg-brand-50/60">
          <td />
          <td colSpan={7} className="px-4 py-4">
            <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-400">How it is measured</div>
                <p className="mt-1 text-sm text-brand-700">{def.sourceNote}</p>
                {def.staffKeepAlive ? (
                  <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-red-800">
                      Keep this number alive
                    </div>
                    <p className="mt-1 text-sm text-red-800">{def.staffKeepAlive}</p>
                  </div>
                ) : null}
                {def.notMeasurable ? (
                  <div className="mt-3 rounded-md border border-slate-300 bg-slate-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                      Not measurable with today's data
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{def.notMeasurable}</p>
                  </div>
                ) : null}
                {qmWaitingFor(def).map((item) => (
                  <div key={item.id} className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                      {item.effect === "wont"
                        ? "QuickManage will not fill this"
                        : "When QuickManage ships this"}
                    </div>
                    <p className="mt-1 text-sm font-medium text-amber-950">{item.title}</p>
                    <p className="mt-1 text-sm text-amber-900">{item.vendorStatus}</p>
                    <p className="mt-1 text-sm text-amber-900">{item.whenShips}</p>
                  </div>
                ))}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge>{AUTOMATION_LABEL[def.automation]}</Badge>
                  <Badge>{def.division === "dryvan" ? "Dry Van" : def.division === "tanker" ? "Tanker" : "Company"}</Badge>
                  {def.scorecardSection ? <Badge variant="accent">On Scorecard</Badge> : <Badge>Framework only</Badge>}
                  {currentEntry?.source === "auto" && <Badge variant="success">Computed automatically</Badge>}
                  {currentEntry?.source === "manual" && def.automation !== "manual" && (
                    <Badge variant="accent">Manual override</Badge>
                  )}
                </div>
                {currentEntry?.source === "auto" && (
                  <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-green-800">
                      Automatic computation
                    </div>
                    {currentEntry.computedAt && (
                      <p className="mt-1 text-xs text-green-700">
                        Computed {new Date(currentEntry.computedAt).toLocaleString()}
                      </p>
                    )}
                    {currentEntry.detail && Object.keys(currentEntry.detail).length > 0 && (
                      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                        {Object.entries(currentEntry.detail).map(([key, detailValue]) => (
                          <div key={key} className="contents">
                            <dt className="font-medium capitalize text-green-800">
                              {key.replaceAll("_", " ")}
                            </dt>
                            <dd className="text-green-700">{formatDetailValue(detailValue)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                )}
                {def.flags?.length ? (
                  <p className="mt-3 text-xs text-amber-700">Open flags: {def.flags.join(" · ")}</p>
                ) : null}
                {LOG_KPI_IDS.has(def.id) && (
                  <KpiLogPanel
                    kpiId={def.id}
                    periodType={periodType}
                    periodStart={periodStart}
                    currentEntry={currentEntry}
                    userId={userId}
                    readOnly={readOnly}
                    onDerivedChange={onLogSynced}
                  />
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-400">Recent history</div>
                {history.length ? (
                  <div className="mt-2 grid gap-1">
                    {history.map((entry) => (
                      <div key={`${entry.periodType}-${entry.periodStart}`} className="flex justify-between text-sm">
                        <span className="text-brand-500">{entry.periodStart}</span>
                        <span className="font-medium">{formatKpiValue(entry.value, def.unit)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-brand-400">No period history yet.</p>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}
